import React from 'react';
import { useQuery } from '@tanstack/react-query';
import type { UseQueryWrapper } from 'app/types';
import { useSnakeBar } from '../../useSnakeBar';
import { toolsServices } from '../../../Domain/Tools/Services';

export interface RelatedEvent {
  '@id': string;
  '@type': string;
  id: string;
  eventType: string;
  eventDate: string;
  state: string;
  processed: boolean;
  archived: boolean;
  createdAt: string;
  updatedAt: string;
  eventSource?: string;
  eventFlowStatus?: string;
  employerName?: string;
  employeeName?: string;
  socialSecurityNumber?: string;
  insurerName?: string;
  productName?: string;
  pensionScheme?: string;
  comment?: string;
}

export interface PensioenbijMutationRaw {
  '@id': string;
  '@type': string;
  externalId: string;
  mutationType: string;
  connectionId?: string;
  connectionName?: string;
  connectionUrl?: string;
  employerKVK?: string;
  employerId: string;
  employerName: string;
  employeeId: string;
  employeeName?: string;
  employeeFirstName?: string;
  employeeInitials?: string;
  employeeLastName?: string;
  employeeRefId?: string;
  employeeRefID?: string;
  batchId?: string;
  updatedAt?: string;
  advisorKVK?: string;
  employerPolicyPlanID?: string;
  employerPolicyPlanId?: string;
  employeeDateOfBirth?: string;
  employeeDateOfEmployment?: string;
  employeeDateOfEmploymentEnd?: string | null;
  employeeEmail?: string;
  employeeLanguage?: string;
  instanceId?: string;
  relatedEventId?: string;
  relatedEvent?: RelatedEvent;
  error?: string;
  syncMessages?: string;
  status?: string;
  syncStatus?: string;
  createdAt: string;
  processingDate?: string | null;
}

export interface PensioenbijMutation {
  '@id': string;
  '@type': string;
  externalId: string;
  mutationType: string;
  connectionId: string;
  connectionName: string;
  connectionUrl: string;
  employerKVK: string;
  employerId: string;
  employerName: string;
  employeeId: string;
  employeeName: string;
  employeeRefID: string;
  employeeRefId: string;
  employerPolicyPlanID: string;
  employerPolicyPlanId: string;
  employeeDateOfBirth: unknown;
  employeeDateOfEmployment: unknown;
  employeeDateOfEmploymentEnd: unknown;
  employeeFirstName: string;
  employeeInitials: string;
  employeeLastName: string;
  employeeEmail: string;
  employeeLanguage: string;
  batchId: string;
  updatedAt: string;
  advisorKVK: string;
  instanceId: string;
  relatedEventId: string;
  relatedEvent: RelatedEvent | null;
  error: string;
  syncMessages: string;
  syncStatus: string;
  createdAt: string;
  processingDate: string | null;
}

export function normalizePensioenbijMutation(item: PensioenbijMutationRaw): PensioenbijMutation {
  const firstName = item.employeeFirstName ?? item.employeeName ?? '';
  const employeeRefId = item.employeeRefId ?? item.employeeRefID ?? item.employeeId ?? '';
  const employerPolicyPlanId = item.employerPolicyPlanId ?? item.employerPolicyPlanID ?? '';

  return {
    '@id': item['@id'],
    '@type': item['@type'],
    externalId: item.externalId,
    mutationType: item.mutationType,
    connectionId: item.connectionId ?? '',
    connectionName: item.connectionName ?? '',
    connectionUrl: item.connectionUrl ?? '',
    employerKVK: item.employerKVK ?? '',
    employerId: item.employerId ?? '',
    employerName: item.employerName ?? '',
    employeeId: item.employeeId ?? '',
    employeeName: item.employeeName ?? '',
    employeeRefID: employeeRefId,
    employeeRefId,
    employerPolicyPlanID: employerPolicyPlanId,
    employerPolicyPlanId,
    employeeDateOfBirth: item.employeeDateOfBirth ?? null,
    employeeDateOfEmployment: item.employeeDateOfEmployment ?? null,
    employeeDateOfEmploymentEnd: item.employeeDateOfEmploymentEnd ?? null,
    employeeFirstName: firstName,
    employeeInitials: item.employeeInitials ?? '',
    employeeLastName: item.employeeLastName ?? '',
    employeeEmail: item.employeeEmail ?? '',
    employeeLanguage: item.employeeLanguage ?? '',
    batchId: item.batchId ?? '',
    updatedAt: item.updatedAt ?? '',
    advisorKVK: item.advisorKVK ?? '',
    instanceId: item.instanceId ?? '',
    relatedEventId: item.relatedEventId ?? '',
    relatedEvent: item.relatedEvent ?? null,
    error: item.error ?? '',
    syncMessages: item.syncMessages ?? '',
    syncStatus: item.syncStatus ?? item.status ?? '',
    createdAt: item.createdAt,
    processingDate: item.processingDate ?? null,
  };
}

type GetPensioenbijEventsQueryVariables = {
  page?: number;
  pageSize?: number;
  status?: string[];
  searchQuery?: string;
  employerId?: string;
  mutationType?: string;
};

type GetPensioenbijEventsQueryResultData = {
  data: PensioenbijMutation[];
  totalItems: number;
};

const queryFn =
  ({ page, pageSize, status, searchQuery, employerId, mutationType }: GetPensioenbijEventsQueryVariables) =>
  async () => {
    const response = await toolsServices.getPensioenbijEvents({
      page,
      pageSize,
      status,
      searchQuery,
      employerId,
      mutationType,
    });
    return response.data;
  };

export const PensioenBijEventsQueryKey = 'getPensioenbijEvents';

export const usePensioenbijUnSyncEventsGetAllQuery: UseQueryWrapper<
  GetPensioenbijEventsQueryResultData,
  GetPensioenbijEventsQueryVariables
> = ({ variables, options }) => {
  const { showErrorSnakeBar } = useSnakeBar();
  const onError = React.useCallback(() => {
    showErrorSnakeBar({ method: PensioenBijEventsQueryKey });
  }, [showErrorSnakeBar]);

  const select = React.useCallback(
    (data: { 'hydra:member': PensioenbijMutationRaw[]; 'hydra:totalItems': number }) => ({
      data: (data['hydra:member'] ?? []).map(normalizePensioenbijMutation),
      totalItems: data['hydra:totalItems'] ?? 0,
    }),
    [],
  );

  return useQuery<any>([PensioenBijEventsQueryKey, ...Object.values(variables || {})], queryFn(variables), {
    select,
    onError,
    ...options,
  });
};
